import { ContentState } from "draft-js";
import { Api } from "@core/api/api";
import { filterParams, pageParams } from "@core/api/pageParams";
import { Page, PaginationOptions } from "@utils/page";
import { contentStateToString } from "@utils/richText/converters";
import { CountryCode } from "@domain/currentProfile/countryCode";
import { ContentLicense } from "@domain/licence/contentLicense";
import { Category } from "@domain/lms/category/category";
import { ContentLanguage } from "@domain/lms/contentLanguage";
import { ContentLevel } from "@domain/lms/contentLevel";
import { CourseDuration, CourseProvider } from "@domain/lms/course/course";
import { OrganizationSubType, OrganizationType } from "@domain/organization/organizationType";
import { PostDto, postFromDto } from "@domain/post/dtos";
import { Post } from "@domain/post/post";
import { Scoring } from "@domain/ranking/ranking";
import { groupDraftToDto, GroupDto, groupFromDto, GroupMemberDto, groupMemberFromDto } from "./dtos";
import { FeedbackDto, feedbackFromDto } from "@domain/group/feedback/dto";
import { Feedback, FeedbackInput } from "@domain/group/feedback/feedback";
import { Group, GroupAsset, GroupMemberRole, GroupMemberStatus, GroupType } from "./group";
import { GroupDraft } from "./groupDraft";
import { GroupMember } from "./groupMember";
import { aboutShareroomDraftToDto, DataChartFromDto, dtoToChart } from "@domain/group/shareroom/dto";
import { AboutShareroomDraft, Period, ShareroomPricing } from "@domain/group/shareroom/shareroom";

const GROUP_URL = "/groups";

export class GroupApi {
	constructor(private readonly api: Api) {}

	async getMyGroups(
		type: GroupType,
		pageOptions: PaginationOptions,
		memberRole?: GroupMemberRole | "ALL"
	): Promise<Page<Group, unknown>> {
		const result = await this.api.get<Page<GroupDto>>(
			`${GROUP_URL}?${pageParams(pageOptions)}&type=${type}&lastViewed=DESC&filter=mine${
				memberRole ? `&memberRole=${memberRole}` : ""
			}`
		);
		return { ...result.data, content: result.data.content.map((group) => groupFromDto(group)) };
	}

	async getUserGroups(profileId: string, pageOptions: PaginationOptions): Promise<Page<Group, unknown>> {
		const result = await this.api.get<Page<GroupDto>>(`${GROUP_URL}?userId=${profileId}&${pageParams(pageOptions)}`);
		return { ...result.data, content: result.data.content.map((group) => groupFromDto(group)) };
	}

	async get(id: string): Promise<Group> {
		const result = await this.api.get<GroupDto>(`${GROUP_URL}/${id}?sortLastView=true`);
		return groupFromDto(result.data);
	}

	async getAssets(id: string, pageOptions: PaginationOptions, type: string): Promise<Page<GroupAsset>> {
		const result = await this.api.get<Page<GroupAsset>>(
			`${GROUP_URL}/${id}/assets?types=${type}&${pageParams(pageOptions)}`
		);
		return result.data;
	}

	async getBySlug(slug: string): Promise<Group> {
		const result = await this.api.get<GroupDto>(`${GROUP_URL}?slug=${slug}&sortLastView=true`);
		return groupFromDto(result.data);
	}
	async create(draft: GroupDraft, memberIds?: string[]): Promise<Group> {
		const result = await this.api.post<GroupDto>(GROUP_URL, { ...groupDraftToDto(draft), memberIds });
		return groupFromDto(result.data);
	}
	async save(draft: GroupDraft, id: string): Promise<Group> {
		const result = await this.api.put<GroupDto>(`${GROUP_URL}/${id}`, groupDraftToDto(draft));
		return groupFromDto(result.data);
	}

	async saveShareroom(draft: AboutShareroomDraft, id: string): Promise<Group> {
		const result = await this.api.put<GroupDto>(`${GROUP_URL}/${id}/classroom`, aboutShareroomDraftToDto(draft));
		return groupFromDto(result.data);
	}

	async saveShareroomPricing(draft: ShareroomPricing, id: string): Promise<Group> {
		const result = await this.api.put<GroupDto>(`${GROUP_URL}/${id}/pricing`, draft);
		return groupFromDto(result.data);
	}

	delete(id: string): Promise<Group> {
		return this.api.delete(`${GROUP_URL}/${id}`);
	}

	async share(groupId: string, message?: ContentState, feedGroupId?: string): Promise<Post> {
		const result = await this.api.post<PostDto>(`${GROUP_URL}/${groupId}/share`, {
			text: message ? contentStateToString(message) : undefined,
			groupId: feedGroupId,
		});
		return postFromDto(result.data);
	}

	async getMember(groupId: string, userId: string): Promise<GroupMember> {
		const result = await this.api.get<GroupMemberDto>(`${GROUP_URL}/${groupId}/members/${userId}`);
		return groupMemberFromDto(result.data);
	}

	async getMembers(
		id: string,
		pageOptions: PaginationOptions,
		role: GroupMemberRole | "pending"
	): Promise<Page<GroupMember>> {
		const validatedStatus: GroupMemberStatus = "VALIDATED";
		const pendingStatuses: GroupMemberStatus[] = ["PENDING_GUEST_ACCEPTANCE", "PENDING_HOST_ACCEPTANCE"];

		const result = await this.api.get<Page<GroupMemberDto>>(
			role === "pending"
				? `${GROUP_URL}/${id}/members?${pageParams(pageOptions)}&statuses=${pendingStatuses.join(",")}`
				: `${GROUP_URL}/${id}/members?${pageParams(pageOptions)}&roles=${role}&statuses=${validatedStatus}`
		);
		return { ...result.data, content: result.data.content.map((member) => groupMemberFromDto(member)) };
	}

	async getShareroomLastFiveMembers(id: string): Promise<Page<GroupMember>> {
		const result = await this.api.get<Page<GroupMemberDto>>(`${GROUP_URL}/${id}/last-five-members`);
		return { ...result.data, content: result.data.content.map((member) => groupMemberFromDto(member)) };
	}

	inviteMember(id: string, userId: string, role: GroupMemberRole): Promise<void> {
		return this.api.post(`${GROUP_URL}/${id}/invite`, { userId, role });
	}

	requestMembership(id: string): Promise<void> {
		return this.api.post(`${GROUP_URL}/${id}/request-join`);
	}

	acceptMembership(groupId: string, userId: string, role: GroupMemberRole): Promise<void> {
		return this.api.post(`${GROUP_URL}/${groupId}/accept-join`, { userId, role });
	}

	deleteMember(id: string, userId: string, role: GroupMemberRole): Promise<void> {
		return this.api.delete(`${GROUP_URL}/${id}/members`, { params: { userId, role } });
	}

	joinGroup(id: string): Promise<void> {
		return this.api.post(`${GROUP_URL}/${id}/join`);
	}

	leaveGroup(id: string): Promise<void> {
		return this.api.post(`${GROUP_URL}/${id}/leave`);
	}

	async suggest(pageOptions: PaginationOptions, categories: Category[], groupType: GroupType): Promise<Page<Group>> {
		const result = await this.api.get<Page<GroupDto>>(
			`${GROUP_URL}/suggested?${pageParams(pageOptions)}&categories=${categories.join(
				","
			)}&type=${groupType}&fieldSort=license`
		);
		return { ...result.data, content: result.data.content.map((group) => groupFromDto(group)) };
	}

	async getActifMemberCount(id: string): Promise<{ members: number }> {
		const result = await this.api.get<{ members: number }>(`${GROUP_URL}/${id}/count-members`);
		return result.data;
	}

	async getSubscriptionsInPeriod(id: string, period: Period): Promise<Array<DataChartFromDto>> {
		const result = await this.api.get<Array<{ date: string; number: number }>>(
			`${GROUP_URL}/${id}/subscriptions-in-period/${period}`
		);
		return dtoToChart(result.data, period);
	}

	async search(
		groupType: GroupType,
		pageOptions: PaginationOptions,
		query: string,
		categories?: Category[],
		levels?: ContentLevel[],
		languages?: ContentLanguage[],
		durations?: CourseDuration[],
		providers?: CourseProvider[],
		orgTypes?: OrganizationType[],
		orgSubTypes?: OrganizationSubType[],
		countries?: CountryCode[],
		license?: ContentLicense[],
		scoring?: Scoring[],
		rating?: number
	): Promise<Page<Group>> {
		const result = await this.api.get<Page<GroupDto>>(
			`${GROUP_URL}?query=${query}&${pageParams(pageOptions)}&${filterParams(
				categories,
				levels,
				languages,
				durations,
				providers,
				orgTypes,
				orgSubTypes,
				countries,
				license,
				scoring,
				rating
			)}&type=${groupType}`
		);
		return { ...result.data, content: result.data.content.map((group) => groupFromDto(group)) };
	}

	async sendFeedback(groupId: string, feedback: FeedbackInput): Promise<void> {
		await this.api.post(`${GROUP_URL}/${groupId}/feedbacks`, feedback);
	}

	async getMyFeedback(groupId: string, feedbackId: string): Promise<Feedback | null> {
		const result = await this.api.get<FeedbackDto>(`${GROUP_URL}/${groupId}/feedbacks/mine/${feedbackId}`);
		return result.data && feedbackFromDto(result.data);
	}

	async listFeedbacks(groupId: string, pageOptions: PaginationOptions): Promise<Page<Feedback>> {
		const result = await this.api.get<Page<FeedbackDto>>(
			`${GROUP_URL}/${groupId}/feedbacks?${pageParams(pageOptions)}`
		);
		return { ...result.data, content: result.data.content.map((dto) => feedbackFromDto(dto)) };
	}
	async lastFeedbacks(groupId: string, size?: number): Promise<Array<Feedback>> {
		const result = await this.api.get<Array<FeedbackDto>>(
			`${GROUP_URL}/${groupId}/feedbacks/recent-feedbacks?size=${size ?? 3}`
		);
		return result.data.map((feedback) => feedbackFromDto(feedback));
	}

	async deleteFeedback(groupId: string, groupFeedbackId: string): Promise<void> {
		await this.api.delete(`${GROUP_URL}/${groupId}/feedbacks/${groupFeedbackId}`);
	}
}
